/**
 * API Error Types
 *
 * Comprehensive type definitions for handling API errors with proper type safety.
 * These types eliminate the need for `as any` type assertions when working with errors.
 */

/**
 * Base API error shape from the backend
 */
export interface ApiErrorResponse {
  detail?: string;
  message?: string;
  status?: number;
  statusText?: string;
  data?: {
    detail?: string;
    message?: string;
    [key: string]: unknown;
  };
  response?: {
    data?: {
      detail?: string;
      message?: string;
      [key: string]: unknown;
    };
    status?: number;
    statusText?: string;
  };
}

/**
 * Extended error with additional properties
 */
export interface ApiError extends Error {
  status?: number;
  statusText?: string;
  data?: unknown;
  isUnauthorized?: boolean;
  response?: {
    data?: {
      detail?: string;
      message?: string;
      [key: string]: unknown;
    };
    status?: number;
    statusText?: string;
  };
}

/**
 * Type guard to check if an error is an API error with status
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    error !== null &&
    typeof error === 'object' &&
    ('status' in error || 'isUnauthorized' in error || 'response' in error)
  );
}

/**
 * Type guard to check if an error is auth-related (401 or 403).
 *
 * Use this for retry logic — neither status should be retried, since they are
 * terminal for the current session/permission state. Do NOT use this to decide
 * navigation targets: 401 means the session is gone (→ /login, handled globally
 * by ApiClient's onUnauthorized), while 403 means the session is valid but the
 * user lacks permission for the resource (→ /unauthorized). For navigation
 * decisions based on a specific status, use `isForbiddenError` or check
 * `error.status === 401` explicitly.
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (!isApiError(error)) {
    return false;
  }

  // Check direct properties
  if (error.isUnauthorized === true) {
    return true;
  }

  if (error.status === 401 || error.status === 403) {
    return true;
  }

  // Check response object
  if (error.response?.status === 401 || error.response?.status === 403) {
    return true;
  }

  // Check error name
  if (error.name === 'UnauthorizedError') {
    return true;
  }

  return false;
}

/**
 * Type guard for 403 Forbidden errors only.
 *
 * A 403 means the session is valid but the user lacks permission for the
 * requested resource — the correct UX is to show the `/unauthorized` page
 * (not /login). 401 is deliberately excluded here: session expiry is handled
 * globally by `ApiClient` which redirects to /login via `onUnauthorized`.
 * Components should not race that flow by navigating to /unauthorized on 401.
 */
export function isForbiddenError(error: unknown): boolean {
  if (!isApiError(error)) {
    return false;
  }

  if (error.status === 403) {
    return true;
  }

  if (error.response?.status === 403) {
    return true;
  }

  return false;
}

/**
 * Type guard to check if error has a detail message
 */
export function hasErrorDetail(error: unknown): error is ApiErrorResponse {
  return (
    error !== null &&
    typeof error === 'object' &&
    ('detail' in error ||
      ('data' in error && error.data !== null && typeof error.data === 'object' && 'detail' in error.data) ||
      ('response' in error &&
        error.response !== null &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data !== null &&
        typeof error.response.data === 'object' &&
        'detail' in error.response.data))
  );
}

/**
 * Extracts error message from various error shapes
 */
export function getErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (typeof error === 'string') {
    return error;
  }

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as ApiErrorResponse;

  // Try different paths to get the error message
  if (err.response?.data?.detail) {
    return err.response.data.detail;
  }

  if (err.data?.detail) {
    return err.data.detail;
  }

  if (err.detail) {
    return err.detail;
  }

  if (err.response?.data?.message) {
    return err.response.data.message;
  }

  if (err.data?.message) {
    return err.data.message;
  }

  if (err.message) {
    return err.message;
  }

  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return fallback;
}

/**
 * Type for TanStack Query error callbacks
 */
export type QueryErrorHandler = (error: ApiError) => void;

/**
 * Type for retry function in TanStack Query
 */
export type QueryRetryFunction = (failureCount: number, error: unknown) => boolean;

/**
 * Standard retry logic for queries - don't retry on unauthorized errors
 */
export const defaultQueryRetry: QueryRetryFunction = (failureCount, error) => {
  // Don't retry on unauthorized errors
  if (isUnauthorizedError(error)) {
    return false;
  }

  // Retry up to 3 times for other errors
  return failureCount < 3;
};
